import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Loader2, CheckCircle2, XCircle, AlertTriangle, Upload, FileSpreadsheet,
  Plus, History, ChevronDown, ChevronRight, Wrench,
} from 'lucide-react';
import { toast } from 'sonner';
import { manualStatementAPI } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';


function PreviewTable({ rows }) {
  if (!rows || rows.length === 0) return null;

  return (
    <div className="overflow-x-auto border rounded-lg max-h-[400px] overflow-y-auto">
      <table className="w-full text-xs">
        <thead className="sticky top-0">
          <tr className="border-b bg-muted/50">
            <th className="text-left px-3 py-1.5 font-medium">Row</th>
            <th className="text-left px-3 py-1.5 font-medium">Date</th>
            <th className="text-left px-3 py-1.5 font-medium">Label</th>
            <th className="text-right px-3 py-1.5 font-medium">Amount</th>
            <th className="text-left px-3 py-1.5 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} className="border-b last:border-0">
              <td className="px-3 py-1.5 text-muted-foreground tabular-nums">{row.raw_row_index}</td>
              <td className="px-3 py-1.5 font-mono tabular-nums">{row.date}</td>
              <td className="px-3 py-1.5 max-w-[300px] truncate">{row.label}</td>
              <td className={`px-3 py-1.5 text-right font-mono tabular-nums ${
                parseFloat(row.amount) < 0 ? 'text-red-600' : 'text-green-600'
              }`}>
                {row.amount}
              </td>
              <td className="px-3 py-1.5">
                {row.is_duplicate ? (
                  <Badge variant="outline" className="text-[10px] text-muted-foreground">duplicate</Badge>
                ) : (
                  <Badge variant="default" className="bg-green-600 hover:bg-green-700 text-[10px]">new</Badge>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function ImportHistoryTable({ imports }) {
  if (!imports || imports.length === 0) {
    return <p className="text-xs text-muted-foreground">No imports yet.</p>;
  }

  return (
    <div className="overflow-x-auto border rounded-lg">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className="text-left px-3 py-1.5 font-medium">Date</th>
            <th className="text-left px-3 py-1.5 font-medium">File</th>
            <th className="text-left px-3 py-1.5 font-medium">Account</th>
            <th className="text-right px-3 py-1.5 font-medium">Inserted</th>
            <th className="text-right px-3 py-1.5 font-medium">Skipped</th>
          </tr>
        </thead>
        <tbody>
          {imports.map((imp, idx) => (
            <tr key={idx} className="border-b last:border-0">
              <td className="px-3 py-1.5 font-mono tabular-nums">
                {imp.imported_at ? new Date(imp.imported_at).toLocaleString() : '-'}
              </td>
              <td className="px-3 py-1.5">{imp.filename || '-'}</td>
              <td className="px-3 py-1.5">{imp.account_id}</td>
              <td className="px-3 py-1.5 text-right tabular-nums text-green-600">{imp.inserted}</td>
              <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">{imp.skipped_duplicates}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


export function ManualStatementImportPanel() {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // entity_uuid backfill (mis-write recovery): read-only preview by
  // default, apply is a separate explicit action.
  const [showBackfill, setShowBackfill] = useState(false);
  const [backfillPreview, setBackfillPreview] = useState(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillApplying, setBackfillApplying] = useState(false);

  // Create account form state
  const [newAccountName, setNewAccountName] = useState('');
  const [newBankName, setNewBankName] = useState('Banque BCP');
  const [newEntityId, setNewEntityId] = useState('');
  const [newCurrency, setNewCurrency] = useState('EUR');
  const [newIban, setNewIban] = useState('');
  const [creating, setCreating] = useState(false);

  const loadAccounts = useCallback(async () => {
    try {
      const { data } = await manualStatementAPI.listAccounts();
      setAccounts(data.accounts || []);
    } catch (err) {
      // Non-blocking; accounts list may be empty on first use
    }
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const handleCreateAccount = async () => {
    if (!newAccountName.trim() || !newBankName.trim() || !newEntityId.trim()) {
      toast.error('Account name, bank name, and entity are required');
      return;
    }
    setCreating(true);
    try {
      const formData = new FormData();
      formData.append('account_name', newAccountName.trim());
      formData.append('bank_name', newBankName.trim());
      formData.append('entity_id', newEntityId.trim());
      formData.append('currency', newCurrency.trim());
      if (newIban.trim()) formData.append('iban', newIban.trim());

      const { data } = await manualStatementAPI.createAccount(formData);
      toast.success(
        data.already_existed
          ? `Account ${data.account_id} already exists`
          : `Account ${data.account_id} created`
      );
      setShowCreate(false);
      setSelectedAccount(data.account_id);
      await loadAccounts();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const handlePreview = async () => {
    if (!selectedAccount || !file) {
      toast.error('Select an account and a CSV file');
      return;
    }
    setLoading(true);
    setPreview(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('account_id', selectedAccount);

      const { data } = await manualStatementAPI.preview(formData);
      setPreview(data);
      toast.success(`Parsed ${data.row_count} rows (${data.new_count} new, ${data.duplicate_count} duplicates)`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!selectedAccount || !file) return;
    if (!preview || preview.new_count === 0) {
      toast.error('No new rows to import');
      return;
    }

    setApplying(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('account_id', selectedAccount);

      const { data } = await manualStatementAPI.apply(formData);
      toast.success(`Imported ${data.inserted} transactions (${data.skipped_duplicates} duplicates skipped)`);
      setPreview(null);
      setFile(null);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  const loadHistory = async () => {
    try {
      const { data } = await manualStatementAPI.history(selectedAccount || '');
      setHistory(data.imports || []);
      setShowHistory(true);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const loadBackfillPreview = async () => {
    setBackfillLoading(true);
    try {
      const { data } = await manualStatementAPI.entityUuidBackfillPreview();
      setBackfillPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBackfillLoading(false);
    }
  };

  const handleBackfillApply = async () => {
    setBackfillApplying(true);
    try {
      const { data } = await manualStatementAPI.entityUuidBackfillApply();
      toast.success(`Updated ${data.updated} account(s)${data.unresolvable ? `, ${data.unresolvable} unresolvable` : ''}`);
      await loadBackfillPreview();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBackfillApplying(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5" />
          Manual Bank Statement Import
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Import bank statements from CSV for accounts not covered by Open Banking
          (e.g. Banque BCP). French CSV conventions supported (semicolon delimiter,
          comma decimal, dd/mm/yyyy).
        </p>

        {/* Account selector */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Account</label>
            <select
              className="border rounded-md px-3 py-2 text-xs bg-muted/20 focus:outline-none focus:ring-2 focus:ring-ring min-w-[200px]"
              value={selectedAccount}
              onChange={(e) => { setSelectedAccount(e.target.value); setPreview(null); }}
            >
              <option value="">-- select account --</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.account_name} ({a.bank_name}, {a.entity_name})
                </option>
              ))}
            </select>
          </div>

          <div className="pt-4">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowCreate(!showCreate)}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              New Account
            </Button>
          </div>

          <div className="pt-4">
            <Button
              size="sm"
              variant="outline"
              onClick={loadHistory}
            >
              <History className="h-3.5 w-3.5 mr-1" />
              History
            </Button>
          </div>
        </div>

        {/* Create account form */}
        {showCreate && (
          <div className="border rounded-lg p-3 space-y-3 bg-muted/20">
            <p className="text-xs font-medium">Create Manual Account</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Account Name</label>
                <input
                  className="border rounded-md px-3 py-1.5 text-xs w-full focus:outline-none focus:ring-2 focus:ring-ring"
                  value={newAccountName}
                  onChange={(e) => setNewAccountName(e.target.value)}
                  placeholder="e.g. BCP Compte Courant"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Bank Name</label>
                <input
                  className="border rounded-md px-3 py-1.5 text-xs w-full focus:outline-none focus:ring-2 focus:ring-ring"
                  value={newBankName}
                  onChange={(e) => setNewBankName(e.target.value)}
                  placeholder="e.g. Banque BCP"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Entity ID</label>
                <input
                  className="border rounded-md px-3 py-1.5 text-xs w-full focus:outline-none focus:ring-2 focus:ring-ring"
                  value={newEntityId}
                  onChange={(e) => setNewEntityId(e.target.value)}
                  placeholder="e.g. bi_sas"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Currency</label>
                <input
                  className="border rounded-md px-3 py-1.5 text-xs w-full focus:outline-none focus:ring-2 focus:ring-ring"
                  value={newCurrency}
                  onChange={(e) => setNewCurrency(e.target.value)}
                  placeholder="EUR"
                />
              </div>
              <div className="space-y-1 col-span-2">
                <label className="text-xs text-muted-foreground">IBAN (optional)</label>
                <input
                  className="border rounded-md px-3 py-1.5 text-xs w-full focus:outline-none focus:ring-2 focus:ring-ring"
                  value={newIban}
                  onChange={(e) => setNewIban(e.target.value)}
                  placeholder="FR76 ..."
                />
              </div>
            </div>
            <Button size="sm" onClick={handleCreateAccount} disabled={creating}>
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
              Create Account
            </Button>
          </div>
        )}

        {/* File upload + preview */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">CSV File</label>
            <input
              type="file"
              accept=".csv,.txt"
              className="border rounded-md px-3 py-1.5 text-xs bg-muted/20 focus:outline-none"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
            />
          </div>
          <div className="pt-4">
            <Button
              size="sm"
              onClick={handlePreview}
              disabled={loading || !selectedAccount || !file}
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
              Preview
            </Button>
          </div>
        </div>

        {/* Preview results */}
        {preview && (
          <div className="space-y-3">
            <div className="border rounded-lg p-3 bg-muted/20 space-y-2">
              <p className="text-xs font-medium">Preview: {preview.filename}</p>
              <div className="flex items-center gap-4 flex-wrap text-xs">
                <span>Encoding: <span className="font-mono">{preview.encoding}</span></span>
                <span>Delimiter: <span className="font-mono">{preview.delimiter === ';' ? 'semicolon' : preview.delimiter === ',' ? 'comma' : 'tab'}</span></span>
                <span>Total rows: <Badge variant="outline" className="text-[10px] tabular-nums">{preview.row_count}</Badge></span>
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3 text-green-600" />
                  New: <Badge variant="default" className="bg-green-600 hover:bg-green-700 text-[10px] tabular-nums">{preview.new_count}</Badge>
                </span>
                <span className="flex items-center gap-1">
                  <XCircle className="h-3 w-3 text-muted-foreground" />
                  Duplicates: <Badge variant="outline" className="text-[10px] tabular-nums">{preview.duplicate_count}</Badge>
                </span>
                {preview.closing_balance && (
                  <span>Closing balance: <span className="font-mono tabular-nums">{preview.closing_balance}</span></span>
                )}
              </div>

              {/* Column mapping */}
              {preview.column_mapping && (
                <div className="text-xs text-muted-foreground">
                  Columns detected: {Object.entries(preview.column_mapping)
                    .filter(([, v]) => v !== null)
                    .map(([k, v]) => `${k.replace('_col', '')}=${preview.headers?.[v] || v}`)
                    .join(', ')}
                </div>
              )}

              {/* Parse errors */}
              {preview.parse_errors?.length > 0 && (
                <div className="flex items-start gap-2 text-xs text-amber-600">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <div>
                    {preview.parse_errors.map((e, i) => (
                      <div key={i}>{e}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <PreviewTable rows={preview.rows} />

            {/* Apply button */}
            {preview.new_count > 0 && (
              <Button
                onClick={handleApply}
                disabled={applying}
              >
                {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
                Import {preview.new_count} new transaction{preview.new_count !== 1 ? 's' : ''}
              </Button>
            )}
          </div>
        )}

        {/* Import history */}
        {showHistory && history && (
          <div className="space-y-2">
            <p className="text-xs font-medium flex items-center gap-2">
              <History className="h-3.5 w-3.5" />
              Import History
            </p>
            <ImportHistoryTable imports={history} />
          </div>
        )}

        {/* entity_uuid backfill (mis-write recovery) */}
        <div className="border-t pt-4">
          <button
            type="button"
            onClick={() => {
              const next = !showBackfill;
              setShowBackfill(next);
              if (next && !backfillPreview) loadBackfillPreview();
            }}
            className="flex items-center gap-2 text-sm font-medium hover:text-foreground text-muted-foreground"
          >
            {showBackfill ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
            <Wrench className="h-3.5 w-3.5" />
            Entity UUID Backfill
          </button>

          {showBackfill && (
            <div className="mt-3 space-y-3">
              <p className="text-xs text-muted-foreground">
                Manually-created accounts used to store the entity slug (e.g. "bi_sas")
                in entity_uuid instead of the real UUID. Read-only preview below;
                apply rewrites entity_uuid from the entity registry.
              </p>

              {backfillLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading...
                </div>
              ) : backfillPreview && backfillPreview.count === 0 ? (
                <p className="text-xs text-green-700 flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  No mis-stamped accounts found.
                </p>
              ) : backfillPreview ? (
                <div className="space-y-3">
                  {backfillPreview.scan_truncated && (
                    <p className="text-xs text-amber-600 flex items-center gap-1.5">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      Showing {backfillPreview.count} of {backfillPreview.total_mis_stamped} mis-stamped
                      accounts -- this list is truncated, apply again after this batch to catch the rest.
                    </p>
                  )}
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left px-2 py-1.5 font-medium">Account</th>
                          <th className="text-left px-2 py-1.5 font-medium">entity_id</th>
                          <th className="text-left px-2 py-1.5 font-medium">Current entity_uuid</th>
                          <th className="text-left px-2 py-1.5 font-medium">Correct entity_uuid</th>
                        </tr>
                      </thead>
                      <tbody>
                        {backfillPreview.accounts.map((a) => (
                          <tr key={a.account_id} className="border-b last:border-0">
                            <td className="px-2 py-1.5">{a.account_name || a.account_id}</td>
                            <td className="px-2 py-1.5 font-mono">{a.entity_id}</td>
                            <td className="px-2 py-1.5 font-mono text-red-600">{a.current_entity_uuid}</td>
                            <td className="px-2 py-1.5 font-mono text-green-700">
                              {a.resolvable ? a.correct_entity_uuid : (
                                <span className="text-amber-600">unresolvable entity_id</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Button size="sm" onClick={handleBackfillApply} disabled={backfillApplying}>
                    {backfillApplying ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Wrench className="h-3.5 w-3.5 mr-1.5" />}
                    Apply Backfill ({backfillPreview.count})
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
